import { useMemo, useState } from "react";
import MenuSearch from "./MenuSearch";
import MenuFilter from "./MenuFilter";
import MenuInfo from "./MenuInfo";
import MenuPerson from "./MenuPerson";
import MenuTrip from "./MenuTrip";

// Each entry is one icon in the top-right row, ordered right-to-left. When
// clicked, its button slides to the top-left corner (staying visible) while
// the other buttons fade out and the close (X) button fades in at top-right.
// Their row position is computed from their index.
//
// The person and trip items are only in the row while something is selected;
// they come last in the ordering, so adding them extends the row leftwards and
// leaves the always-present buttons where they were.
const person_item = {
  key: "person",
  icon: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4.4 3.6-7 8-7s8 2.6 8 7" />
    </svg>
  )
};

// The artwork only occupies the middle band of the 24-unit grid, so the viewBox
// is cropped to it - otherwise this icon reads as half the size of its
// neighbours at the same width.
const trip_item = {
  key: "trip",
  icon: (
    <svg viewBox="0 5 24 13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12h22c-0.5 3-2 5-4 5H3a2 2 0 0 1-2-2z" />
      <path d="M4 12V8h15v4" />
      <line x1="17" y1="8" x2="17" y2="6" />
    </svg>
  )
};

const always_menu_items = [
  {
    key: "info",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="10" x2="12" y2="20" />
        <line x1="12" y1="4" x2="12" y2="4" />
      </svg>
    )
  },
  {
    key: "filter",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="4 4 20 4 14 12 14 19 10 21 10 12 4 4" />
      </svg>
    )
  },
  {
    key: "search",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <circle cx="11" cy="11" r="7" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
    )
  }
];

// The i-th button (0-indexed, right-to-left) sits in the slot that is
// (i + 1) button-widths in from the right edge of the header.
const closed_left = (index) => {
  return `calc(100% - ${index + 1} * (var(--header_button_size) + 1vmin))`;
};

const Menu = ({ people, trips, selected_person, selected_trip, set_selected_person, set_selected_trip, suppress_hover_after_menu_select, person_indices_filter, trip_indices_filter, set_filter, set_filter_active, nora_mode, set_nora_mode }) => {

  const [active_key, set_active_key] = useState(null);

  // If a trip or person is selected, add its menu item to the row.
  // If both trip and person selected, only show trip button.
  const menu_items = useMemo(() => {
    const items = always_menu_items.slice();
    if (selected_trip !== null) {
      items.push(trip_item);
    } else if (selected_person !== null) {
      items.push(person_item);
    }
    return items;
  }, [selected_person, selected_trip]);

  // The open menu's button can disappear from the row (its person or trip got
  // deselected), in which case nothing is open any more.
  const active = menu_items.some((item) => item.key === active_key) ? active_key : null;

  const close_menu = () => {
    suppress_hover_after_menu_select();
    set_active_key(null);
  };

  // Only close when the backdrop or the empty part of the header is clicked,
  // not when the click bubbles up from a button or the menu panel content.
  const on_background_click = (e) => {
    e.stopPropagation();
    if (e.target === e.currentTarget && active !== null) close_menu();
  };

  return (
    <div className={"menu_overlay" + (active !== null ? " menu_overlay_active" : "")}
         onClick={on_background_click}>
      <div className="header slide_in_down" onClick={on_background_click}>
        {menu_items.map((item, index) => {
          const is_active = active === item.key;
          const is_hidden = active !== null && !is_active;
          const button_class = "header_button menu_item_button" + (is_hidden ? " menu_item_hidden" : "");
          return (
            <button key={item.key} className={button_class}
                    style={{ left: is_active ? "1vmin" : closed_left(index) }}
                    onClick={() => set_active_key(is_active ? null : item.key)}>
              {item.icon}
            </button>
          );
        })}
        <button className={"header_button close_button" + (active !== null ? " close_button_visible" : "")}
                onClick={close_menu}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="6" y1="6" x2="18" y2="18" />
            <line x1="18" y1="6" x2="6" y2="18" />
          </svg>
        </button>
      </div>
      <MenuPerson active={active === "person"} people={people}
                  selected_person={selected_person}
                  set_selected_trip={set_selected_trip} close_menu={close_menu} />
      <MenuTrip active={active === "trip"} trips={trips}
                selected_trip={selected_trip}
                set_selected_person={set_selected_person} close_menu={close_menu} />
      <MenuSearch active={active === "search"} people={people} trips={trips}
                  set_selected_person={set_selected_person} set_selected_trip={set_selected_trip}
                  close_search={close_menu} />
      <MenuFilter active={active === "filter"} people={people} trips={trips}
                  person_indices_filter={person_indices_filter} trip_indices_filter={trip_indices_filter}
                  set_filter={set_filter} set_filter_active={set_filter_active} />
      <MenuInfo active={active === "info"} people={people} trips={trips}
                close_menu={close_menu}
                nora_mode={nora_mode} set_nora_mode={set_nora_mode} />
    </div>
  );
};

export default Menu;
